import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import {
	getFiscalReportListRequest,
	downloadFiscalPdfRequest,
	downloadFiscalCsvRequest,
	downloadFiscalXlsxRequest,
	exportFiscalXlsxRequest,
	sendPaidRequest,
	sendReportRequest,
	cancelReportRequest,
} from 'redux/reducers/fiscalReports/reducer';
import {
	selectFiscalReportListData,
	selectFiscalReportListLoader,
	selectFiscalReportListTotal,
	selectFiscalReportList,
	selectDownloadFiscalPdfLoader,
	selectDownloadFiscalCsvLoader,
	selectDownloadFiscalXlsxLoader,
	selectExportFiscalXlsxLoader,
	selectUpdateFiscalReportPaymentLoader,
} from 'redux/reducers/fiscalReports/selectors';
import { PAGE_SIZE } from 'services/constants/env';
import { TFiscalReportListItem } from 'redux/reducers/fiscalReports/types';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TTableStructure } from 'ui/EasTable/types';
import { TFiltersQ4Us } from 'redux/types';
import { EReportType, EReportTypeDownload } from 'redux/reducers/fiscalReports/constants';
import { EasParamsBox } from 'components/EMManagement/EMManagementId/EasParamsBox';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { EditFiscalReportDialog } from './EditFiscalReportDialog';
import { tableStructure } from './tableStructure';
import { EditCommentDialog } from './EditCommentDialog';
import { boxStructure } from './boxStructure';
import { getFiltersFromNavigateParams } from '../../utils/FilterParams';

export const FiscalReports: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [openEditCommentDialog, setOpenEditCommentDialog] = useState(false);
	const [selectedFiscalReport, setSelectedFiscalReport] = useState<TFiscalReportListItem | null>(
		null,
	);
	const [openCancelReportDialog, setOpenCancelReportDialog] = useState(false);
	const [openSendReportDialog, setOpenSendReportDialog] = useState(false);
	const [openSubmitAllPayedDialog, setOpenSubmitAllPayedDialog] = useState(false);
	const [openSendZeroReportDialog, setOpenSendZeroReportDialog] = useState(false);
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TFiscalReportListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);
	// const handleTableStructureChange = undefined;

	const fiscalReportListData = useSelector(selectFiscalReportListData);
	const fiscalReportListLoader = useSelector(selectFiscalReportListLoader);
	const fiscalReportListTotal = useSelector(selectFiscalReportListTotal);
	const fiscalReportList = useSelector(selectFiscalReportList);
	const downloadFiscalPdfLoader = useSelector(selectDownloadFiscalPdfLoader);
	const downloadFiscalCsvLoader = useSelector(selectDownloadFiscalCsvLoader);
	const downloadFiscalXlsxLoader = useSelector(selectDownloadFiscalXlsxLoader);
	const exportFiscalXlsxLoader = useSelector(selectExportFiscalXlsxLoader);
	const updateFiscalReportPaymentLoader = useSelector(selectUpdateFiscalReportPaymentLoader);

	const navigateParamsWithoutPage = useMemo(() => {
		return getFiltersFromNavigateParams(currentTableStructure, navigateParams);
	}, [currentTableStructure, navigateParams]);

	// const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
	// 	if (!!navigateParams[item.selector] && item.type === ETableStructureType.DATE) {
	// 		if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
	// 			if (navigateParams[`${String(item.selector)}StartDate`]) {
	// 				acc.push({
	// 					name: item.selector,
	// 					value: navigateParams[`${String(item.selector)}StartDate`] as string,
	// 					comparator: '>=',
	// 				});
	// 			}
	// 			if (navigateParams[`${String(item.selector)}EndDate`]) {
	// 				acc.push({
	// 					name: item.selector,
	// 					value: navigateParams[`${String(item.selector)}EndDate`] as string,
	// 					comparator: '<=',
	// 				});
	// 			}
	// 			return acc;
	// 		}
	// 		const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
	// 		// console.log('dates', dates);
	// 		if (dates?.from) {
	// 			acc.push({
	// 				name: item.selector,
	// 				value: dates.from,
	// 				comparator: '>=',
	// 			});
	// 		}
	// 		if (dates?.to) {
	// 			acc.push({
	// 				name: item.selector,
	// 				value: dates.to,
	// 				comparator: '<=',
	// 			});
	// 		}
	// 		return acc;
	// 	}
	// 	if (item.type === ETableStructureType.AMOUNT && navigateParams[item.selector]) {
	// 		acc.push({
	// 			name: item.selector,
	// 			value: navigateParams[item.selector] as string,
	// 			comparator: '=',
	// 		});
	// 		return acc;
	// 	}
	// 	if (navigateParams[item.selector])
	// 		acc.push({
	// 			name: item.selector,
	// 			value: navigateParams[item.selector] as string,
	// 			comparator: navigateParams[item.selector] === 'null' ? 'isNull' : '=',
	// 		});
	// 	return acc;
	// }, [] as TFiltersQ4Us);

	// if (navigateParams.searchEmId) {
	// 	navigateParamsWithoutPage.push({
	// 		name: 'em_id',
	// 		value: navigateParams.searchEmId as string,
	// 		comparator: '=',
	// 	});
	// }

	// if (navigateParams.f_scheme_id) {
	// 	navigateParamsWithoutPage.push({
	// 		name: 'scheme_id',
	// 		value: navigateParams.f_scheme_id as string,
	// 		comparator: '=',
	// 	});
	// }

	// const getFiscalReportListRequestPayload = useCallback(() => {
	// 	const orderBy = [];
	// 	if (navigateParams?.sortIndex) {
	// 		orderBy.push({
	// 			fieldName: navigateParams?.sortIndex
	// 				? tableStructure[+navigateParams.sortIndex].selector
	// 				: 'id',
	// 			direction: navigateParams?.sortDirection
	// 				? String(navigateParams.sortDirection) === 'desc'
	// 					? ESortDirection.DESC
	// 					: ESortDirection.ASC
	// 				: ESortDirection.DESC,
	// 		} as TOrderBy);
	// 	}
	// 	orderBy.push({
	// 		fieldName: 'id',
	// 		direction: ESortDirection.DESC,
	// 	} as TOrderBy);

	// 	return {
	// 		tableName: 'fiscal_vat_report_list',
	// 		orderBy: orderBy.map((item) => `${item.fieldName} ${item.direction}`),
	// 		filter: navigateParamsWithoutPage,
	// 		page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
	// 	};
	// }, [navigateParams, navigateParamsWithoutPage, page]);

	// useEffect(() => {
	// 	dispatch(getFiscalReportListRequest(getFiscalReportListRequestPayload()));
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [dispatch, search]);

	const orderByNavigateParams = useMemo(() => {
		const orderBy: string[] = [];
		if (navigateParams?.sortIndex) {
			const sortItem = tableStructure[+navigateParams.sortIndex];
			if (sortItem) {
				orderBy.push(
					`${sortItem.selector} ${navigateParams?.sortDirection === 'desc' ? 'desc' : 'asc'}`,
				);
			}
		}
		return orderBy;
	}, [navigateParams]);

	useEffect(() => {
		const navigateParamsWithoutPage = getFiltersFromNavigateParams(
			currentTableStructure,
			navigateParams,
		);
		dispatch(
			getFiscalReportListRequest({
				tableName: 'fiscal_vat_report_list',
				orderBy: orderByNavigateParams.length ? orderByNavigateParams : ['id desc'],
				filter: navigateParamsWithoutPage as TFiltersQ4Us,
				page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleDownloadFiscalPdf = useCallback(
		(emId: number, month: number, year: number) => {
			dispatch(downloadFiscalPdfRequest({ emId, month, year }));
		},
		[dispatch],
	);

	const handleDownloadFiscalCsv = useCallback(
		(emId: number, month: number, year: number, countryCode: string, scheme: string) => {
			const type = scheme === EReportTypeDownload.IOSS && countryCode === 'EE' ? 'xml' : 'csv';

			dispatch(downloadFiscalCsvRequest({ emId, month, year, countryCode, scheme, type }));
		},
		[dispatch],
	);

	const handleDownloadFiscalXlsx = useCallback(
		(emId: number, month: number, year: number, scheme: string) => {
			dispatch(downloadFiscalXlsxRequest({ emId, month, year, scheme }));
		},
		[dispatch],
	);

	const handleExportFiscalXlsx = useCallback(() => {
		dispatch(
			exportFiscalXlsxRequest({
				tableName: 'fiscal_vat_report_list',
				filter: navigateParamsWithoutPage as TFiltersQ4Us,
				templateName: 'fiscal_vat',
			}),
		);
	}, [dispatch, navigateParamsWithoutPage]);

	const handleSubmitAllPayed = useCallback(() => {
		dispatch(sendPaidRequest());
	}, [dispatch]);

	const handleSendZeroReport = useCallback(() => {
		if (selectedFiscalReport?.global_id) {
			dispatch(
				sendReportRequest({
					reportId: selectedFiscalReport?.global_id,
					sendZero: true,
					fiscalReportListRequest: {
						tableName: 'fiscal_vat_report_list',
						filter: navigateParamsWithoutPage as TFiltersQ4Us,
						page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
					},
				}),
			);
			setOpenSendZeroReportDialog(false);
			setSelectedFiscalReport(null);
		}
	}, [dispatch, selectedFiscalReport, navigateParamsWithoutPage, page]);

	const handleAction = useCallback(
		(id: number, typeOfDialog: string) => {
			if (typeOfDialog === 'pdf') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					handleDownloadFiscalPdf(item.em_id, item.report_period, item.year);
				}
			} else if (typeOfDialog === 'csv' || typeOfDialog === 'xml') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					const countryCode = item.scheme_registered_country_code || 'EE';
					const scheme =
						item.report_type === EReportType.IOSS
							? EReportTypeDownload.IOSS
							: item.report_type === EReportType.NON_EU_IOSS
								? EReportTypeDownload.NON_EU_IOSS
								: item.report_type;
					handleDownloadFiscalCsv(item.em_id, item.report_period, item.year, countryCode, scheme);
				}
			} else if (typeOfDialog === 'xlsx') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					const scheme =
						item.report_type === EReportType.IOSS
							? EReportTypeDownload.IOSS
							: item.report_type === EReportType.NON_EU_IOSS
								? EReportTypeDownload.NON_EU_IOSS
								: item.report_type;
					handleDownloadFiscalXlsx(item.em_id, item.report_period, item.year, scheme);
				}
			} else if (typeOfDialog === 'Mark as Filed') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					setSelectedFiscalReport(item);
					setOpenEditDialog(true);
				}
			} else if (typeOfDialog === 'comment') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					setSelectedFiscalReport(item);
					setOpenEditCommentDialog(true);
				}
			} else if (typeOfDialog === 'submit') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					setSelectedFiscalReport(item);
					setOpenSendReportDialog(true);
				}
			} else if (typeOfDialog === 'cancel') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					setSelectedFiscalReport(item);
					setOpenCancelReportDialog(true);
				}
			} else if (typeOfDialog === 'Zero') {
				const item = fiscalReportListData.find((fiscalItem) => fiscalItem.id === id);
				if (item) {
					setSelectedFiscalReport(item);
					setOpenSendZeroReportDialog(true);
				}
			}
		},
		[
			handleDownloadFiscalPdf,
			handleDownloadFiscalCsv,
			handleDownloadFiscalXlsx,
			fiscalReportListData,
		],
	);

	return (
		<Box
			sx={{
				margin: '0px 10px 20px 10px',
				// maxWidth: 'calc(100vw - 20px)',
				// overflowX: 'auto',
			}}
		>
			<Box sx={{ width: 'auto', minWidth: '800px', marginBottom: 1 }}>
				<EasParamsBox<{
					id: number;
					vat_amount_reported: number;
					vat_amount_to_be_reported: number;
					total: number;
				}>
					boxStructure={boxStructure}
					items={{
						id: 0,
						vat_amount_reported: fiscalReportList?.page.vat_amount_reported || 0,
						vat_amount_to_be_reported: fiscalReportList?.page.vat_amount_to_be_reported || 0,
						total: fiscalReportList?.page.total || 0,
					}}
					columnNumber={3}
					editable={false}
				/>
			</Box>

			<EasTableWithFilter<TFiscalReportListItem>
				tableStructure={currentTableStructure}
				items={{
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					totalCount: fiscalReportList?.page?.total ?? 0,
					data: fiscalReportListData,
				}}
				itemsIsLoading={
					fiscalReportListLoader ||
					downloadFiscalPdfLoader ||
					downloadFiscalCsvLoader ||
					downloadFiscalXlsxLoader ||
					exportFiscalXlsxLoader ||
					updateFiscalReportPaymentLoader
				}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box
					sx={{
						width: 'auto',
						minWidth: '800px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 1,
					}}
				>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={handleExportFiscalXlsx}
						disabled={exportFiscalXlsxLoader}
					>
						Export to Excel
					</Button>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => setOpenSubmitAllPayedDialog(true)}
						disabled={updateFiscalReportPaymentLoader}
					>
						Submit all paid reports
					</Button>
				</Box>
			</EasTableWithFilter>
			<EditFiscalReportDialog
				open={openEditDialog}
				setClose={() => {
					setOpenEditDialog(false);
					setSelectedFiscalReport(null);
				}}
				data={selectedFiscalReport}
				fiscalReportListRequestPayload={{
					tableName: 'fiscal_vat_report_list',
					filter: navigateParamsWithoutPage as TFiltersQ4Us,
					page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
				}}
			/>
			<EditCommentDialog
				open={openEditCommentDialog}
				setClose={() => {
					setOpenEditCommentDialog(false);
					setSelectedFiscalReport(null);
				}}
				data={selectedFiscalReport}
				fiscalReportListRequestPayload={{
					tableName: 'fiscal_vat_report_list',
					filter: navigateParamsWithoutPage as TFiltersQ4Us,
					page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
				}}
			/>
			<ConfirmationDialog
				open={openCancelReportDialog}
				setClose={() => {
					setOpenCancelReportDialog(false);
				}}
				title="Cancel Report"
				text="Are you sure you want to cancel the report?"
				handleConfirm={() => {
					if (selectedFiscalReport?.id) {
						dispatch(
							cancelReportRequest({
								reportId: selectedFiscalReport?.global_id,
								fiscalReportListRequest: {
									tableName: 'fiscal_vat_report_list',
									filter: navigateParamsWithoutPage as TFiltersQ4Us,
									page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
								},
							}),
						);
						setOpenCancelReportDialog(false);
						setSelectedFiscalReport(null);
					}
				}}
			/>
			<ConfirmationDialog
				open={openSendReportDialog}
				setClose={() => {
					setOpenSendReportDialog(false);
				}}
				title="Submit Report"
				text="Are you sure you want to submit the report?"
				handleConfirm={() => {
					if (selectedFiscalReport?.global_id) {
						dispatch(
							sendReportRequest({
								reportId: selectedFiscalReport?.global_id,
								fiscalReportListRequest: {
									tableName: 'fiscal_vat_report_list',
									filter: navigateParamsWithoutPage as TFiltersQ4Us,
									page: { size: PAGE_SIZE, from: (page ? +page : 1) * PAGE_SIZE - PAGE_SIZE },
								},
							}),
						);
						setOpenSendReportDialog(false);
						setSelectedFiscalReport(null);
					}
				}}
			/>
			<ConfirmationDialog
				open={openSubmitAllPayedDialog}
				setClose={() => {
					setOpenSubmitAllPayedDialog(false);
				}}
				title="Submit all payed reports"
				text="Are you sure you want to submit all payed reports?"
				handleConfirm={() => {
					handleSubmitAllPayed();
					setOpenSubmitAllPayedDialog(false);
				}}
			/>
			<ConfirmationDialog
				open={openSendZeroReportDialog}
				setClose={() => {
					setOpenSendZeroReportDialog(false);
				}}
				title="Submit Report with Zero"
				text="Are you sure you want to submit the report with zero?"
				handleConfirm={handleSendZeroReport}
			/>
		</Box>
	);
};
